/**
 * @typedef {module:plugins-omemo-index.WindowWithLibsignal} WindowWithLibsignal
 * @typedef {import('@converse/headless/shared/types').MessageAttributes} MessageAttributes
 * @typedef {import('@converse/headless/plugins/muc/types').MUCMessageAttributes} MUCMessageAttributes
 * @typedef {import('@converse/headless').ChatBox} ChatBox
 * @typedef {import('@converse/headless/types/shared/message').default} BaseMessage
 */
import { html } from 'lit';
import { __ } from 'i18n';
import { until } from 'lit/directives/until.js';
import { _converse, api, log, u, constants } from '@converse/headless';
import tplAudio from 'shared/texture/templates/audio.js';
import tplFile from 'templates/file.js';
import tplImage from 'shared/texture/templates/image.js';
import tplVideo from 'shared/texture/templates/video.js';
import { MIMETYPES_MAP } from 'utils/file.js';
import { getFileName } from 'utils/html.js';

const { CHATROOMS_TYPE } = constants;
const { hexToArrayBuffer, isAudioURL, isError, isImageURL, isVideoURL } = u;

/**
 * @param {string} fp
 */
export function formatFingerprint(fp) {
    fp = fp.replace(/^05/, '');
    for (let i = 1; i < 8; i++) {
        const idx = i * 8 + i - 1;
        fp = fp.slice(0, idx) + ' ' + fp.slice(idx);
    }
    return fp;
}

/**
 * @param {string} fp
 */
export function formatFingerprintForQRCode(fp) {
    const sid = _converse.state.omemo_store.get('device_id');
    const jid = _converse.session.get('bare_jid');
    fp = fp.replace(/^05/, '');
    return `xmpp:${jid}?omemo-sid-${sid}=${fp}`;
}

/**
 * @param {string} iv
 * @param {string} key
 * @param {ArrayBuffer} cipher
 */
async function decryptFile(iv, key, cipher) {
    const key_obj = await crypto.subtle.importKey('raw', hexToArrayBuffer(key), 'AES-GCM', false, ['decrypt']);
    const algo = /** @type {AesGcmParams} */ {
        name: 'AES-GCM',
        iv: hexToArrayBuffer(iv),
    };
    return crypto.subtle.decrypt(algo, key_obj, cipher);
}

/**
 * Some upload servers store files under a content-addressed/random path
 * (no real filename in the URL at all) and instead name the file via a
 * `Content-Disposition` response header. Without this, such a file gets
 * saved locally under its bare storage key (e.g. a UUID, no extension) --
 * which looks "corrupted" since nothing recognizes it as e.g. an .exe.
 * @param {string|null} header
 * @returns {string|null}
 */
function parseContentDispositionFilename(header) {
    if (!header) return null;
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(header);
    if (!match) return null;
    const [, encoded, plain] = match;
    try {
        return encoded ? decodeURIComponent(encoded) : plain;
    } catch {
        return plain ?? null;
    }
}

/**
 * @param {string} url
 * @returns {Promise<{buffer: ArrayBuffer, filename: string|null}|null>}
 */
async function downloadFile(url) {
    let response;
    try {
        response = await fetch(url);
    } catch (e) {
        log.error(`${e.name}: Failed to download encrypted media: ${url}`);
        log.error(e);
        return null;
    }

    if (response.status >= 200 && response.status < 400) {
        return {
            buffer: await response.arrayBuffer(),
            // Only readable if the server sends
            // `Access-Control-Expose-Headers: Content-Disposition` on a
            // cross-origin response -- absent that, this is just `null` and
            // we fall back to the URL path, same as before.
            filename: parseContentDispositionFilename(response.headers.get('content-disposition')),
        };
    }
}

/**
 * @param {string} url_text
 * @returns {Promise<string|Error|null>}
 */
/**
 * @param {string} url_text
 * @returns {Promise<string|Error|null>}
 */
async function getAndDecryptFile(url_text) {
    log.info('OMEMO Media: Начинаем процесс дешифровки файла: ' + url_text);

    const url = new URL(url_text);
    const protocol = window.location.hostname === 'localhost' && url.hostname === 'localhost' ? 'http' : 'https';
    const http_url = url.toString().replace(/^aesgcm/, protocol);

    log.info('OMEMO Media: Загрузка зашифрованных данных по URL: ' + http_url);
    const downloaded = await downloadFile(http_url);

    // 404/мережева помилка → downloadFile повертає null АБО undefined. Перевіряємо
    // обидва (раніше було лише === null), інакше код падав на cipher.byteLength нижче.
    if (!downloaded || typeof downloaded.buffer?.byteLength !== 'number') {
        log.error(`OMEMO Media: Не удалось скачать файл по ссылке ${url.toString()}`);
        return new Error(__('Error: could not decrypt a received encrypted file, because it could not be downloaded'));
    }
    const cipher = downloaded.buffer;

    log.info('OMEMO Media: Файл загружен, размер (байт): ' + cipher.byteLength);

    // --- FIX SECTION START ---
    const rawHash = url.hash.slice(1);
    log.info('OMEMO Media: Исходный хеш из URL: ' + rawHash);

    // Очищаем хеш от суффиксов (например, _encrypted)
    const cleanHash = rawHash.split('_')[0];
    log.info('OMEMO Media: Очищенный хеш (hex): ' + cleanHash);

    // Извлекаем ключ и IV
    const key = cleanHash.substring(cleanHash.length - 64);
    const iv = cleanHash.replace(key, '');

    log.info('OMEMO Media: Извлеченный IV (hex): ' + iv + ' (длина: ' + iv.length + ')');
    log.info('OMEMO Media: Извлеченный Key (hex): ' + key + ' (длина: ' + key.length + ')');
    // --- FIX SECTION END ---

    let content;
    try {
        log.info('OMEMO Media: Запуск crypto.subtle.decrypt...');
        content = await decryptFile(iv, key, cipher);
        log.info('OMEMO Media: Дешифровка WebCrypto прошла успешно!');
    } catch (e) {
        log.error(`OMEMO Media: Ошибка выполнения decryptFile для ${url.toString()}`);
        log.error(e);
        return null;
    }

    const [filename, extension] = url.pathname.split('/').pop().split('.');
    const mimetype = MIMETYPES_MAP[extension] || 'application/octet-stream';
    log.info(`OMEMO Media: Тип файла: ${extension}, MIME: ${mimetype}`);

    try {
        const file = new File([content], filename, { 'type': mimetype });
        const objectUrl = URL.createObjectURL(file);
        log.info('OMEMO Media: Blob URL создан успешно: ' + objectUrl);
        return objectUrl;
    } catch (e) {
        log.error(`OMEMO Media: Ошибка при создании Blob/File для ${url.toString()}`);
        log.error(e);
        return null;
    }
}

/**
 * @param {string} file_url
 * @param {string|Error} obj_url
 * @param {import('shared/texture/texture.js').Texture} richtext
 * @returns {import("lit").TemplateResult}
 */
function getTemplateForObjectURL(file_url, obj_url, richtext) {
    if (isError(obj_url)) {
        // Акуратний читабельний плейсхолдер замість сирого червоного тексту
        // (детальна помилка — у title як підказка).
        const msg = /** @type {Error} */ (obj_url).message;
        return html`<span class="sonata-file-error" title="${msg}"><converse-icon class="fa fa-circle-exclamation" size="1em"></converse-icon><span>${__('Файл більше не доступний')}</span></span>`;
    }

    if (isImageURL(file_url)) {
        return tplImage({
            src: obj_url,
            onClick: richtext.onImgClick,
            onLoad: richtext.onImgLoad,
        });
    } else if (isAudioURL(file_url)) {
        return tplAudio(/** @type {string} */ (obj_url));
    } else if (isVideoURL(file_url)) {
        return tplVideo(/** @type {string} */ (obj_url));
    } else {
        return tplFile(obj_url, getFileName(file_url));
    }
}

/**
 * @param {string} text
 * @param {number} offset
 * @param {import('shared/texture/texture.js').Texture} richtext
 */
function addEncryptedFiles(text, offset, richtext) {
    const objs = [];
    try {
        const parse_options = { start: /\b(aesgcm:\/\/)/gi };
        u.withinString(
            text,
            /**
             * @param {string} url
             * @param {number} start
             * @param {number} end
             */
            (url, start, end) => {
                objs.push({ url, start, end });
                return url;
            },
            parse_options,
        );
    } catch (error) {
        log.debug(error);
        return;
    }
    objs.forEach((o) => {
        const promise = getAndDecryptFile(o.url).then((obj_url) => getTemplateForObjectURL(o.url, obj_url, richtext));

        const template = html`${until(promise, '')}`;
        richtext.addTemplateResult(o.start + offset, o.end + offset, template);
    });
}

/**
 * @param {import('shared/texture/texture.js').Texture} richtext
 */
export function handleEncryptedFiles(richtext) {
    if (!_converse.state.config.get('trusted')) {
        return;
    }
    richtext.addAnnotations(
        /**
         * @param {string} text
         * @param {number} offset
         */
        (text, offset) => addEncryptedFiles(text, offset, richtext),
    );
}

export function onChatComponentInitialized(el) {
    const is_muc = el.model.get('type') === CHATROOMS_TYPE;

    if (is_muc) {
        // 1. Принудительно выключаем активность при инициализации
        el.model.set('omemo_active', false);

        // 2. Слушаем изменения: если что-то (другой плагин или логика)
        // пытается включить OMEMO в этой комнате — сбрасываем обратно.
        el.listenTo(el.model, 'change:omemo_active', (model, value) => {
            if (value === true) {
                log.info('OMEMO: Force-disabling encrypted outbound for MUC');
                model.set('omemo_active', false);
            }
        });
    }

    el.listenTo(el.model.messages, 'add', (message) => {
        // Мы оставляем omemo_supported, чтобы входящие могли расшифроваться
        if (message.get('is_encrypted') && !message.get('is_error')) {
            el.model.save('omemo_supported', true);
        }
    });

    el.listenTo(el.model, 'change:omemo_supported', () => {
        // Если это MUC, мы игнорируем активацию, но обновляем тулбар
        if (is_muc) {
            el.querySelector('converse-chat-toolbar')?.requestUpdate();
            return;
        }

        if (!el.model.get('omemo_supported') && el.model.get('omemo_active')) {
            el.model.set('omemo_active', false);
        } else {
            el.querySelector('converse-chat-toolbar')?.requestUpdate();
        }
    });

    el.listenTo(el.model, 'change:omemo_active', () => {
        el.querySelector('converse-chat-toolbar')?.requestUpdate();
    });
}

/**
 * @param {string} jid
 */
export async function generateFingerprints(jid) {
    const devices = await u.omemo.getDevicesForContact(jid);
    return Promise.all(devices.map((d) => u.omemo.generateFingerprint(d)));
}

/**
 * @param {string} jid
 * @param {string} device_id
 * @returns {Promise<import('@converse/headless').Device[]>}
 */
export async function getDeviceForContact(jid, device_id) {
    const devices = await u.omemo.getDevicesForContact(jid);
    return devices.get(device_id);
}

/**
 * @param {MouseEvent} ev
 */
function toggleOMEMO(ev) {
    ev.stopPropagation();
    ev.preventDefault();
    const toolbar_el = u.ancestor(ev.target, 'converse-chat-toolbar');
    if (!toolbar_el.model.get('omemo_supported')) {
        let messages;
        if (toolbar_el.model.get('type') === CHATROOMS_TYPE) {
            messages = [
                __(
                    'Cannot use end-to-end encryption in this groupchat, ' +
                        'either the groupchat has some anonymity or not all participants support OMEMO.',
                ),
            ];
        } else {
            messages = [
                __(
                    "Cannot use end-to-end encryption because %1$s uses a client that doesn't support OMEMO.",
                    toolbar_el.model.contact.getDisplayName(),
                ),
            ];
        }
        return api.alert('error', __('Error'), messages);
    }
    toolbar_el.model.save({ 'omemo_active': !toolbar_el.model.get('omemo_active') });
}

/**
 * @param {import('shared/chat/toolbar').ChatToolbar} toolbar_el
 * @param {Array<import('lit').TemplateResult>} buttons
 */
export function getOMEMOToolbarButton(toolbar_el, buttons) {
    const model = toolbar_el.model;
    const is_muc = model.get('type') === CHATROOMS_TYPE;
    // if (is_muc) {
    return buttons;
    // }

    let title;
    if (model.get('omemo_supported')) {
        const i18n_plaintext = __('Messages are being sent in plaintext');
        const i18n_encrypted = __('Messages are sent encrypted');
        title = model.get('omemo_active') ? i18n_encrypted : i18n_plaintext;
    } else if (is_muc) {
        title = __(
            'This groupchat needs to be members-only and non-anonymous in ' +
                'order to support OMEMO encrypted messages',
        );
    } else {
        title = __('OMEMO encryption is not supported');
    }

    let color;
    if (model.get('omemo_supported')) {
        if (model.get('omemo_active')) {
            color = is_muc ? `var(--muc-color)` : `var(--chat-color)`;
        } else {
            color = `var(--error-color)`;
        }
    } else {
        color = `var(--disabled-color)`;
    }
    buttons.push(html`<button type="button" class="btn toggle-omemo" title="${title}" data-disabled="${!model.get('omemo_supported')}" @click="${toggleOMEMO}"><converse-icon class="fa ${model.get('omemo_active') ? `fa-lock` : `fa-unlock`}" path-prefix="${api.settings.get('assets_path')}" size="1em" color="${color}"></converse-icon></button>`);
    return buttons;
}

Object.assign(u, {
    omemo: {
        ...u.omemo,
        formatFingerprint,
    },
});
